import React from "react";
import frontendErrorLogger from "@/services/frontendErrorLogger";
import "./ErrorBoundary.css";

/**
 * Error Boundary para capturar errores de renderización de React
 * Captura errores en cualquier componente hijo y los registra automáticamente
 */
class ErrorBoundary extends React.Component {
  constructor(props) {
    super(props);
    this.state = {
      hasError: false,
      error: null,
      errorInfo: null,
      errorCount: 0,
    };
  }

  static getDerivedStateFromError(error) {
    // Actualizar el estado para que el siguiente renderizado muestre la interfaz de error
    return { hasError: true };
  }

  componentDidCatch(error, errorInfo) {
    // Capturar el error y registrarlo
    console.error("Error capturado por ErrorBoundary:", error, errorInfo);

    // Detectar si es un error de carga de chunks (post-deploy)
    if (this.isChunkLoadError(error)) {
      console.warn(
        "[ErrorBoundary] Error de carga de chunk detectado. Recargando para obtener versión actualizada...",
      );
      // Limpiar caches de assets y recargar
      if ("caches" in window) {
        caches.keys().then((names) => {
          const assetCaches = names.filter(
            (n) => n.includes("assets") || n.includes("precache"),
          );
          Promise.all(assetCaches.map((n) => caches.delete(n))).then(() => {
            window.location.reload();
          });
        });
      } else {
        window.location.reload();
      }
      return;
    }

    // Actualizar estado con información del error
    this.setState((prevState) => ({
      error,
      errorInfo,
      errorCount: prevState.errorCount + 1,
    }));

    // Detectar si es un error de token expirado o autenticación
    const isAuthError = this.isAuthenticationError(error);

    // Registrar el error en el sistema de logging solo si no es error de auth
    if (!isAuthError) {
      frontendErrorLogger.handleError({
        error,
        message: error.message || "React component error",
        type: "React Component Error",
        componentStack: errorInfo.componentStack,
      });
    }

    // Si es un error crítico repetido, recargar la página después de un tiempo
    if (this.state.errorCount >= 3) {
      console.error(
        "Múltiples errores detectados. Recomendando recarga de página.",
      );
    }
  }

  // Detectar si el error está relacionado con autenticación
  isAuthenticationError(error) {
    const errorMessage = error?.message?.toLowerCase() || "";
    const errorString = error?.toString()?.toLowerCase() || "";

    return (
      errorMessage.includes("token") ||
      errorMessage.includes("unauthorized") ||
      errorMessage.includes("401") ||
      errorMessage.includes("forbidden") ||
      errorMessage.includes("403") ||
      errorMessage.includes("expired") ||
      errorMessage.includes("authentication") ||
      errorString.includes("token") ||
      errorString.includes("unauthorized") ||
      errorString.includes("expired")
    );
  }

  // Detectar si es un error de carga de chunks (post-deploy)
  isChunkLoadError(error) {
    const errorMessage = error?.message?.toLowerCase() || "";
    const errorString = error?.toString()?.toLowerCase() || "";

    return (
      errorMessage.includes("importing a module script failed") ||
      errorMessage.includes("failed to fetch dynamically imported module") ||
      errorMessage.includes("loading chunk") ||
      errorMessage.includes("loading css chunk") ||
      errorMessage.includes("dynamically imported module") ||
      errorString.includes("chunkerror") ||
      errorString.includes("chunkloaderror")
    );
  }

  handleLogout = () => {
    // Limpiar todo el almacenamiento
    localStorage.clear();
    sessionStorage.clear();

    // Redirigir al login
    window.location.href = "/";
  };

  handleReload = () => {
    window.location.reload();
  };

  handleReset = () => {
    this.setState({
      hasError: false,
      error: null,
      errorInfo: null,
    });

    // Si hay una función de reset personalizada, llamarla
    if (this.props.onReset) {
      this.props.onReset();
    }
  };

  render() {
    if (this.state.hasError) {
      // Si hay un fallback personalizado, usarlo
      if (this.props.fallback) {
        return this.props.fallback({
          error: this.state.error,
          errorInfo: this.state.errorInfo,
          resetError: this.handleReset,
        });
      }

      // Verificar si es un error de autenticación
      const isAuthError = this.isAuthenticationError(this.state.error);

      // Interfaz especial para errores de sesión expirada
      if (isAuthError) {
        return (
          <div className="error-boundary-container">
            <div className="error-boundary-content">
              <div className="session-expired-content">
                <div className="session-expired-icon">
                  <i className="fas fa-user-clock"></i>
                </div>

                <h1 className="error-title">Sesión Expirada</h1>

                <p className="session-description">
                  Tu sesión ha expirado por inactividad o por razones de
                  seguridad. Por favor, inicia sesión nuevamente para continuar.
                </p>

                <button
                  className="btn-logout-error"
                  onClick={this.handleLogout}
                >
                  <i className="fas fa-sign-in-alt"></i>
                  Volver a Iniciar Sesión
                </button>

                <div className="error-details">
                  <details>
                    <summary>Ver detalles técnicos</summary>
                    <div className="error-stack">
                      <strong>Error:</strong>
                      <pre>{this.state.error?.toString()}</pre>

                      {this.state.error?.stack && (
                        <>
                          <strong>Stack Trace:</strong>
                          <pre>{this.state.error.stack}</pre>
                        </>
                      )}
                    </div>
                  </details>
                </div>
              </div>
            </div>
          </div>
        );
      }

      // Interfaz de error por defecto
      return (
        <div className="error-boundary-container">
          <div className="error-boundary-content">
            <div className="error-icon">
              <i className="fas fa-exclamation-triangle"></i>
            </div>

            <h1 className="error-title">¡Oops! Algo salió mal</h1>

            <p className="error-description">
              Ha ocurrido un error inesperado. Nuestro equipo ha sido notificado
              automáticamente y estamos trabajando para solucionarlo.
            </p>

            <div className="error-details">
              <details>
                <summary>Ver detalles técnicos</summary>
                <div className="error-stack">
                  <strong>Error:</strong>
                  <pre>{this.state.error?.toString()}</pre>

                  {this.state.error?.stack && (
                    <>
                      <strong>Stack Trace:</strong>
                      <pre>{this.state.error.stack}</pre>
                    </>
                  )}

                  {this.state.errorInfo?.componentStack && (
                    <>
                      <strong>Component Stack:</strong>
                      <pre>{this.state.errorInfo.componentStack}</pre>
                    </>
                  )}
                </div>
              </details>
            </div>

            <div className="error-actions">
              <button className="btn-primary-error" onClick={this.handleReset}>
                <i className="fas fa-redo"></i>
                Intentar de nuevo
              </button>

              <button
                className="btn-secondary-error"
                onClick={this.handleReload}
              >
                <i className="fas fa-sync"></i>
                Recargar página
              </button>

              <button
                className="btn-tertiary-error"
                onClick={() => (window.location.href = "/")}
              >
                <i className="fas fa-home"></i>
                Ir al inicio
              </button>
            </div>

            {this.state.errorCount >= 2 && (
              <div className="error-warning">
                <i className="fas fa-exclamation-circle"></i>
                <span>
                  Se han detectado múltiples errores. Te recomendamos recargar
                  la página.
                </span>
              </div>
            )}
          </div>
        </div>
      );
    }

    // Si no hay error, renderizar los componentes hijos normalmente
    return this.props.children;
  }
}

// Hook para usar ErrorBoundary de forma más funcional
export const useErrorHandler = () => {
  const [error, setError] = React.useState(null);

  React.useEffect(() => {
    if (error) {
      // Lanzar el error para que sea capturado por ErrorBoundary
      throw error;
    }
  }, [error]);

  return setError;
};

export default ErrorBoundary;
